'use client';

import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
    theme: Theme;
    resolvedTheme: 'light' | 'dark';
    setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Get initial theme from localStorage or default
function getInitialTheme(): Theme {
    if (typeof window !== 'undefined') {
        const saved = localStorage.getItem('theme') as Theme | null;
        return saved || 'system';
    }
    return 'system';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [mounted, setMounted] = useState(false);
    const [theme, setThemeState] = useState<Theme>('system');
    const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('dark');

    // Initialize theme on mount
    useEffect(() => {
        const initialTheme = getInitialTheme();
        setThemeState(initialTheme);
        setMounted(true);
    }, []);

    // Apply theme whenever it changes
    useEffect(() => {
        if (!mounted) return;

        const root = window.document.documentElement;

        const applyTheme = (newTheme: 'light' | 'dark') => {
            root.classList.remove('light', 'dark');
            root.classList.add(newTheme);
            root.style.colorScheme = newTheme;
            setResolvedTheme(newTheme);
        };

        if (theme === 'system') {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            applyTheme(mediaQuery.matches ? 'dark' : 'light');

            const handler = (e: MediaQueryListEvent) => {
                applyTheme(e.matches ? 'dark' : 'light');
            };

            mediaQuery.addEventListener('change', handler);
            return () => mediaQuery.removeEventListener('change', handler);
        } else {
            applyTheme(theme);
        }
    }, [theme, mounted]);

    const setTheme = (newTheme: Theme) => {
        setThemeState(newTheme);
        localStorage.setItem('theme', newTheme);
        document.cookie = `theme=${newTheme}; path=/; max-age=31536000; SameSite=Lax`;
    };

    // Prevent hydration mismatch by always rendering with a consistent initial value
    // The actual theme will be applied after mount via useEffect
    return (
        <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

const defaultThemeContext: ThemeContextType = {
    theme: 'system',
    resolvedTheme: 'dark',
    setTheme: () => {},
};

export function useTheme() {
    const context = useContext(ThemeContext);
    // Return default when context is unavailable (e.g. Turbopack module isolation)
    return context ?? defaultThemeContext;
}

// Script to inject in <head> to prevent flash of wrong theme.
// Accepts a nonce so the inline script passes the proxy's nonce-based CSP
// (proxy.ts emits `script-src 'nonce-...' 'strict-dynamic'`, which makes
// `'unsafe-inline'` get ignored in modern browsers).
export const ThemeScript = ({ nonce }: { nonce?: string | null }) => {
    const script = `
        (function() {
            try {
                var theme = localStorage.getItem('theme') || 'system';
                var resolved = theme;
                if (theme === 'system') {
                    resolved = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
                }
                document.documentElement.classList.add(resolved);
                document.documentElement.style.colorScheme = resolved;
            } catch (e) {}
        })();
    `;
    // suppressHydrationWarning: every HTTP request gets a fresh CSP nonce
    // (per-request rotation in src/proxy.ts), and modern browsers strip the
    // nonce attribute from the DOM once they have validated the script. The
    // mismatch between server-rendered "nonce=<X>" and the post-strip DOM
    // value is benign for security and triggers a noisy dev overlay we
    // would otherwise have to suppress at the page level.
    return (
        <script
            nonce={nonce ?? undefined}
            dangerouslySetInnerHTML={{ __html: script }}
            suppressHydrationWarning
        />
    );
};
